import { useState, useEffect } from "react";
import { HexColorPicker } from "react-colorful";
import {
  useFormContext,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

/** Matches a valid CSS hex colour: #rgb or #rrggbb (case-insensitive). */
export const HEX_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

/** Curated 18-colour palette mirroring the legacy WrappedColourPicker. */
const SWATCHES = [
  "#001f3f",
  "#0074D9",
  "#7FDBFF",
  "#39CCCC",
  "#3D9970",
  "#2ECC40",
  "#01FF70",
  "#FFDC00",
  "#FF851B",
  "#FF4136",
  "#85144b",
  "#F012BE",
  "#B10DC9",
  "#111111",
  "#AAAAAA",
  "#DDDDDD",
  "#FFFFFF",
];

interface ColorPickerFieldProps<TValues extends FieldValues> {
  name: FieldPath<TValues>;
  label: string;
  description?: string;
}

interface ColorPickerContentProps {
  /** The currently committed hex value (e.g. "#ff0000"). */
  value: string;
  errorId: string;
  onChange: (next: string) => void;
}

/** Inner component so hooks can be used outside the render-prop callback. */
function ColorPickerContent({
  value,
  errorId,
  onChange,
}: ColorPickerContentProps) {
  const [hexInput, setHexInput] = useState(value);
  const [hexError, setHexError] = useState<string | null>(null);

  // Keep the text input in sync when the picker or a swatch changes the value.
  // Normalise to lowercase so the text input always displays a canonical form.
  useEffect(() => {
    setHexInput((value ?? "").toLowerCase());
    setHexError(null);
  }, [value]);

  function commitHexInput() {
    const raw = hexInput.trim();
    if (!raw || raw === "#") {
      setHexError(null);
      return;
    }
    const normalised = raw.startsWith("#") ? raw : `#${raw}`;
    if (HEX_REGEX.test(normalised)) {
      onChange(normalised.toLowerCase());
      setHexError(null);
    } else {
      setHexError("Enter a valid hex colour code (e.g. #ff0000 or #f00)");
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setHexInput(e.target.value);
    // Clear any stale error while the user is correcting their input.
    if (hexError) setHexError(null);
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commitHexInput();
    }
  }

  return (
    <>
      <HexColorPicker color={value} onChange={onChange} />
      <div className="mt-3 grid grid-cols-9 gap-1">
        {SWATCHES.map((swatch) => (
          <button
            key={swatch}
            type="button"
            className="h-5 w-5 rounded border border-neutral-300"
            style={{ background: swatch }}
            onClick={() => onChange(swatch.toLowerCase())}
            aria-label={swatch}
          />
        ))}
      </div>
      <div className="mt-3">
        <Input
          type="text"
          aria-label="Hex colour code"
          aria-invalid={!!hexError}
          aria-describedby={hexError ? errorId : undefined}
          value={hexInput}
          onChange={handleInputChange}
          onBlur={commitHexInput}
          onKeyDown={handleInputKeyDown}
          placeholder="#000000"
          spellCheck={false}
          className="font-mono"
        />
        {hexError ? (
          <p className="mt-1 text-xs text-red-600" role="alert" id={errorId}>
            {hexError}
          </p>
        ) : null}
      </div>
    </>
  );
}

export function ColorPickerField<TValues extends FieldValues>({
  name,
  label,
  description,
}: ColorPickerFieldProps<TValues>) {
  const form = useFormContext<TValues>();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => {
        const value = (field.value as string) ?? "#000000";
        const errorId = `${String(name)}-hex-input-error`;
        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label={`${label} colour picker`}
                    className="flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 py-1.5 font-inter text-sm text-neutral-900 transition-colors hover:bg-neutral-50"
                  >
                    <span
                      className="inline-block h-5 w-5 rounded border border-neutral-300"
                      style={{ background: value }}
                    />
                    <span>{value}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3">
                  <ColorPickerContent
                    value={value}
                    errorId={errorId}
                    onChange={(next) => field.onChange(next)}
                  />
                </PopoverContent>
              </Popover>
            </FormControl>
            {description ? (
              <FormDescription>{description}</FormDescription>
            ) : null}
            {fieldState.error ? (
              <FormMessage>{fieldState.error.message}</FormMessage>
            ) : null}
          </FormItem>
        );
      }}
    />
  );
}
